/* eslint-disable arrow-body-style */
import React, { useState, useEffect } from 'react';
import { useIntl, history } from 'umi';
import { Table, TableColumn, PagingQuery } from 'tea-component';
import FirstRow from '@intelligence/components/TableComponents/components/FirstRow';
import TopchartTable from '@intelligence/components/TableComponents/TopchartTable';
import CommonColumns from '@intelligence/pages/TopCharts/_config/TableCloums/CommonCloums';
import { SortByItemType } from '@intelligence/components/TableComponents/components/SortBy';
import { getCountryFlag } from '@home/_config/countries';
import { getDetailGamePathCommon } from '@intelligence/utils/topchartsUtils';
import { SortType } from '@intelligence/pages/TopCharts/_config/enums';
import MobileDetailsOverlapMetricColumn from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overlap/components/Tables/columns';
import { DEFAULT_PAGE_SIZE } from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overlap/constants';
import {
  SORTABLE_METRICS,
  SORTABLE_METRICS_METADATAS_MAPPING,
} from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overlap/constants/metrics';

import type { IGameType } from '@intelligence/types/common';
import type { TopchartTableProps } from '@intelligence/pages/_config/topchartsInterface';
import type {
  IMobileDetailsOverlapRank,
  IMobileDetailsOverlapTopRanksMetrics,
} from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overlap/types';

const { injectable } = Table.addons;

interface IMobileDetailsOverlapTablesContentProps extends TopchartTableProps {
  entityType: IGameType;
  isHeatMap?: boolean;
  genreSource: IGenreSource;
  topRanksMetrics?: IMobileDetailsOverlapTopRanksMetrics;
  setActiveId: React.Dispatch<React.SetStateAction<string>>;
}

const MobileDetailsOverlapTablesContent: React.FC<IMobileDetailsOverlapTablesContentProps> = (props) => {
  const {
    entityType,
    loading = true,
    records = [],
    tableTotal = 0,
    pageSortAttr,
    tableSelection,
    tableSelectionAll,
    isHeatMap,
    genreSource,
    topRanksMetrics,
    setActiveId,
    onTableSortChage,
    onTablePagingChange,
    onTableCheckGame,
    className,
  } = props;

  const { locale, formatMessage } = useIntl();

  const { tablePageIndex, tablePageSize, currentSortValue, currentSortMetric, currentRatioType } = pageSortAttr || {};
  const [pageIndex, setPageIndex] = useState(tablePageIndex || 1);
  const [pageSize, setPageSize] = useState(tablePageSize || DEFAULT_PAGE_SIZE);
  const [selectList, setSelectList] = useState(tableSelection);

  useEffect(() => {
    setPageIndex(tablePageIndex);
    setPageSize(tablePageSize);
  }, [tablePageIndex, tablePageSize]);

  const handlePagingChange = (pagingInfo: PagingQuery) => {
    setPageIndex(pagingInfo?.pageIndex || 1);
    setPageSize(pagingInfo?.pageSize || DEFAULT_PAGE_SIZE);
    onTablePagingChange(pagingInfo);
  };

  const onSortChage = (sortInfo: SortByItemType) => {
    onTableSortChage(sortInfo);
  };

  useEffect(() => {
    setSelectList(tableSelection);
  }, [tableSelection]);

  const getIsCellHighlight = (metric?: string, id?: string) => {
    if (!isHeatMap) return false;
    if (!metric || !id) return false;
    const topRanks = topRanksMetrics?.[metric];
    return topRanks?.includes(id) || false;
  };

  const getMetricCloums = (): TableColumn<IMobileDetailsOverlapRank>[] => {
    return SORTABLE_METRICS.map((key) => {
      const metricSortCfg = SORTABLE_METRICS_METADATAS_MAPPING[key];
      return MobileDetailsOverlapMetricColumn({
        ...metricSortCfg,
        key,
        currentSortKey: `${currentSortMetric}_${currentRatioType}`,
        currentSortValue: currentSortValue as SortType,
        onSortChage,
        entityType,
      });
    });
  };

  const handleClickGame = (id: string) => {
    const path = getDetailGamePathCommon({
      entityType,
      gameId: id || '',
      tab: 'overview',
      subTab: '',
    });
    setActiveId('overview');
    history.push(path);
  };

  return (
    <TopchartTable
      recordKey="id"
      className={className}
      loading={loading}
      records={records}
      tableTotal={tableTotal}
      pageIndex={pageIndex}
      pageSize={pageSize}
      onPagingChange={handlePagingChange}
      tableAddons={[
        injectable({
          cell: (props, context) => {
            const { column, record } = context || {};
            const isCellHighlight = getIsCellHighlight(column?.key, record?.id);
            return {
              className: isCellHighlight ? 'dbi-highlight-bg' : '',
            };
          },
        }),
      ]}
      columns={
        [
          CommonColumns.CheckerColumn({
            disabled: loading || records?.length === 0,
            tableSelectionAll,
            selectList,
            onChange: (list, status) => {
              setSelectList(list);
              onTableCheckGame(list, status);
            },
          }),
          {
            key: 'game',
            fixed: 'left',
            width: 300,
            header: (
              <div className="dbi-companies-table-header-other dbi-firstRow-flex">
                <span className="dbi-companies-table-flex-id dbi-text-id">#</span>
                <span style={{ marginRight: 8 }}>{formatMessage({ id: 'dbi_game' })}</span>
              </div>
            ),
            render: (cvm) => {
              const id = cvm?.unified_id || '';
              const path = getDetailGamePathCommon({
                entityType,
                gameId: id || '',
                tab: 'overview',
                subTab: '',
              });
              return (
                <FirstRow
                  id={cvm?.rank}
                  rihgtClickToJumpHref={path}
                  cvm={{
                    imgType: entityType,
                    gametitle: cvm?.name,
                    img: cvm?.cover,
                    countryIcon: getCountryFlag(cvm?.publisher_addr),
                    text: cvm?.publisher,
                  }}
                  onClick={() => {
                    handleClickGame(cvm?.unified_id);
                  }}
                />
              );
            },
          },
          ...getMetricCloums(),
          CommonColumns.ReleaseTimeColumn({ locale, formatMessage }),
          CommonColumns.GenreColumn({ genreSource: genreSource as any, width: 270 }),
        ] as TableColumn<IMobileDetailsOverlapRank>[]
      }
    />
  );
};

export default React.memo(MobileDetailsOverlapTablesContent);
